import useApiCall from "../hooks/CancellableApiCall";
import {DiscordBotInfoResponse} from "../data/discord/DiscordBotInfoResponse";
import {LoadingContent} from "../components/LoadingContent";
import React, {ReactElement, ReactNode} from "react";
import {Alert, Box, Grid, Paper, Stack, Tab, Typography} from "@mui/material";
import {AvatarBlock, TextBracket} from "../components/profile/ProfileInfo";
import {ProfilePicture} from "../components/PoliceAvatar";
import CheckIcon from '@mui/icons-material/Check';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import {TabContext, TabList, TabPanel} from "@mui/lab";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import WorkIcon from '@mui/icons-material/Work';
import {DiscordRolePanel} from "../components/discord/DiscordRolePanel";
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import {DiscordChannelConfigPanel} from "../components/discord/DiscordChannelConfigPanel";

interface DiscordPanelContentProps {
    bot: DiscordBotInfoResponse
}

interface BotConfigCheckProps {
    met: boolean
    name: string
    failureText: string
    additionalText: number | string
}

const BotConfigCheck = (props: BotConfigCheckProps) => {
    return <Stack spacing={2} direction={"row"}>
        {props.met ? <>
            <CheckIcon color={"success"} />
            <Typography>{props.name}</Typography>
        </>: <>
            <DoNotDisturbIcon color={"error"} />
            <Typography>{props.name} - {props.failureText} ({props.additionalText})</Typography>
        </>}
    </Stack>
}

const DiscordPanelContent = (props: DiscordPanelContentProps) => {
    interface TabContent {
        title: string
        path?: string
        icon: ReactElement
        content: ReactNode
    }

    const tabs: TabContent[] = []
    const [searchParams, setSearchParams] = useSearchParams()
    const navigate = useNavigate()
    const params = useParams()

    let allConditionsMet = props.bot.hasAdmin && props.bot.otherBotCount <= 0 && props.bot.botRoleLevel === 0

    function changeTab(index: number) {
        const tabValue = tabs[index].path
        const newUrl = `/discord-panel/${tabValue}`
        navigate(newUrl)
    }

    tabs.push({
        title: "Role Manager",
        path: "roles",
        icon: <WorkIcon/>,
        content: <DiscordRolePanel />
    })

    tabs.push({
        title: "Channel Configurations",
        path: "channel-configs",
        icon: <DisplaySettingsIcon/>,
        content: <DiscordChannelConfigPanel />
    })

    const tabIndex = tabs.findIndex((t) => t.path === params.tab) ?? 0

    return <Paper variant={"outlined"} style={{
        marginTop: "16px",
        padding: "16px",
    }}>
        {props.bot.online ? <>
            <Stack sx={{mb: 1}}>
                {allConditionsMet ? <Alert variant={"outlined"} severity="success">Discord Bot is online in server {props.bot.currentServer} and correctly configured</Alert>
                    : <Alert variant={"outlined"} severity="warning">Discord Bot is online in server {props.bot.currentServer} but it is miss-configured, it may not function as designed please contact the server owner {props.bot.currentServerOwnerName}</Alert> }
            </Stack>
            <Grid container spacing={2}>
                <Grid item xs={12} md={12} lg={6} xl={4}>
                    <Paper variant={"outlined"} sx={{padding: '15px'}}>
                        <AvatarBlock>
                            <ProfilePicture
                                avatarUrl={props.bot.profilePicture}
                                color={'BF40BF'}
                                dimensions={150}
                                rounded={true}
                            />
                            <TextBracket>
                                <Typography variant="h5" color="text.primary">{props.bot.name}</Typography>
                            </TextBracket>
                        </AvatarBlock>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={12} lg={12} xl={8}>
                    <Paper variant={"outlined"} sx={{padding: '15px'}}>
                        <Typography variant="h5" color="text.primary">Bot Configuration Checklist</Typography>
                        <BotConfigCheck met={props.bot.hasAdmin} name={"Administrator"} failureText={"Please ensure the bot has the administrator permission"} additionalText={"missing permission"} />
                        <BotConfigCheck met={props.bot.botRoleLevel === 0} name={"Role Immunity"} failureText={"Please ensure the bot is the highest ranking member of the server"} additionalText={`Roles higher than the bot: ${props.bot.botRoleLevel}`} />
                        <BotConfigCheck met={props.bot.otherBotCount <= 0} name={"No Conflicting PLPD Bots"} failureText={"Please ensure there are no conflicting bots, this issue should fix itself if the other items are correct"} additionalText={`Conflicting Bots: ${props.bot.otherBotCount}`} />
                    </Paper>
                </Grid>
            </Grid>
            <TabContext value={tabIndex.toString()}>
                <Box sx={{borderBottom: 1, borderColor: "divider", mt:3}}>
                    <TabList onChange={(_, newValue) => {
                        setSearchParams(searchParams)
                        changeTab(parseInt(newValue))
                    }} variant="scrollable" scrollButtons="auto">
                        {tabs.map((data, idx) =>
                            <Tab icon={data.icon} label={data.title} value={idx.toString()} key={data.title}/>
                        )}
                    </TabList>
                </Box>
                {tabs.map((data, idx) =>
                    <TabPanel value={idx.toString()} key={data.title}>
                        {tabIndex === idx ? data.content : <></>}
                    </TabPanel>
                )}
            </TabContext>
            </> : <>
                <Stack sx={{mb: 1}}>
                    <Alert variant={"outlined"} severity="error">Discord Bot is Offline</Alert>
                </Stack>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={12} lg={6} xl={4}>
                        <Paper variant={"outlined"} sx={{padding: '15px'}}>
                            <AvatarBlock>
                                <ProfilePicture
                                    avatarUrl={""}
                                    color={'c66163'}
                                    dimensions={150}
                                    rounded={true}
                                />
                                <TextBracket>
                                    <Typography variant="h5" color="text.primary">Offline Bot</Typography>
                                </TextBracket>
                            </AvatarBlock>
                        </Paper>
                    </Grid>
                </Grid>
            </>}
    </Paper>
}

export const DiscordPanel = () => {
    const discordCall = useApiCall<DiscordBotInfoResponse>({
        url: "/api/discord/"
    })

    return (<LoadingContent isLoading={discordCall.isLoading || !discordCall.data}>
            <DiscordPanelContent bot={discordCall.data!!} />
        </LoadingContent>)
}